import { apiFetch, readError, refreshAfterUnauthorized } from "./client";

/** A channel as the signed-in user sees it. */
export type ChatChannel = {
  id: string;
  name: string;
  topic: string | null;
  privateChannel: boolean;
  defaultChannel: boolean;
  createdBy: string | null;
  createdAt: string;
  member: boolean;
  memberCount: number;
  unread: number;
  mentions: number;
  lastMessageAt: string | null;
  /** Everything after this is new to the user; null outside the channel. */
  lastReadAt: string | null;
  /** For a conversation between two colleagues: the other one. Null for a channel. */
  directWith: string | null;
  canManage: boolean;
};

export type ChatReaction = { emoji: string; users: string[] };

/** A picture or PDF sent with a message; `url` draws it inline and is signed for a while. */
export type ChatAttachment = { id: string; filename: string; contentType: string; sizeBytes: number; url: string | null };

/** What a message may carry, as the server checks it (the type again from the file's bytes). */
export const CHAT_FILE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"];
export const CHAT_MAX_FILES = 5;
export const CHAT_MAX_FILE_BYTES = 20 * 1024 * 1024;
export const CHAT_MAX_FILES_BYTES = 45 * 1024 * 1024;

/** One message. A colleague named in it stands in the body as `<@id>`. */
export type ChatMessage = {
  id: string;
  channelId: string;
  authorId: string;
  body: string;
  mentions: string[];
  createdAt: string;
  editedAt: string | null;
  reactions: ChatReaction[];
  attachments: ChatAttachment[];
  /** For a reply: the message it answers, and that message's author. Null for a message in the channel. */
  parentId: string | null;
  parentAuthorId: string | null;
  /** For a message in the channel: its thread, if it has one (the latest repliers, up to three). */
  replyCount: number;
  lastReplyAt: string | null;
  replyUsers: string[];
  /** Who follows the thread: for a message, the one under it; for a reply, the one it is in. */
  followers: string[];
};

export type ChatPerson = {
  id: string;
  name: string;
  avatarUrl: string | null;
  roles: string[];
  online: boolean;
};

/** The reactions the server accepts, in the order the screen offers them. */
export const CHAT_REACTIONS = ["👍", "❤️", "😂", "🎉", "👀", "✅", "🙏", "🔥", "😮", "😢", "👏", "💯"] as const;

/** How many messages one page brings; a shorter page means the channel has nothing older. */
export const CHAT_PAGE = 50;

/** A thread the user follows: its message (with the thread's summary), the newest reply, and how many are new. */
export type ChatThread = { message: ChatMessage; latest: ChatMessage | null; unread: number };

/** How many followed threads one page of the list of threads brings. */
export const CHAT_THREADS_PAGE = 30;

/** What a search found: a page of it, newest first, and how many messages in all. */
export type ChatSearchResult = { total: number; messages: ChatMessage[] };

/** A search: the words, and the channel or colleague that narrows it. */
export type ChatSearchQuery = { q: string; channelId?: string; authorId?: string; before?: string };

const base = "/chat";

/** A message with files: multipart, refreshed once after a 401 like any other call. */
async function sendFiles(path: string, body: string, files: File[]): Promise<ChatMessage> {
  const send = () => {
    const form = new FormData();
    if (body) form.append("body", body);
    for (const file of files) form.append("files", file, file.name);
    return fetch(`/api${path}`, { method: "POST", credentials: "include", headers: { Accept: "application/json" }, body: form });
  };
  let res = await send();
  if (res.status === 401 && (await refreshAfterUnauthorized())) res = await send();
  if (!res.ok) throw await readError(res);
  return (await res.json()) as ChatMessage;
}

export const chatApi = {
  channels: () => apiFetch<ChatChannel[]>(`${base}/channels`),
  createChannel: (body: { name: string; topic?: string; privateChannel: boolean; memberIds?: string[] }) =>
    apiFetch<ChatChannel>(`${base}/channels`, { method: "POST", body }),
  updateChannel: (id: string, body: { name?: string; topic?: string }) =>
    apiFetch<ChatChannel>(`${base}/channels/${id}`, { method: "PATCH", body }),
  archiveChannel: (id: string) => apiFetch<void>(`${base}/channels/${id}/archive`, { method: "POST" }),
  join: (id: string) => apiFetch<ChatChannel>(`${base}/channels/${id}/join`, { method: "POST" }),
  leave: (id: string) => apiFetch<void>(`${base}/channels/${id}/leave`, { method: "POST" }),
  /** The conversation with one colleague, made the first time either of the two opens it. */
  direct: (userId: string) => apiFetch<ChatChannel>(`${base}/direct`, { method: "POST", body: { userId } }),
  members: (id: string) => apiFetch<string[]>(`${base}/channels/${id}/members`),
  addMembers: (id: string, userIds: string[]) =>
    apiFetch<string[]>(`${base}/channels/${id}/members`, { method: "POST", body: { userIds } }),
  markRead: (id: string) => apiFetch<void>(`${base}/channels/${id}/read`, { method: "POST" }),
  messages: (id: string, before?: string) =>
    apiFetch<ChatMessage[]>(`${base}/channels/${id}/messages?limit=${CHAT_PAGE}${before ? `&before=${before}` : ""}`),
  /** The channel from the given message to the newest, with a few before it; the newest page if it lies too far back. */
  messagesThrough: (id: string, messageId: string) =>
    apiFetch<ChatMessage[]>(`${base}/channels/${id}/messages?through=${messageId}`),
  search: ({ q, channelId, authorId, before }: ChatSearchQuery) => {
    const params = new URLSearchParams({ q, limit: String(CHAT_PAGE) });
    if (channelId) params.set("channelId", channelId);
    if (authorId) params.set("authorId", authorId);
    if (before) params.set("before", before);
    return apiFetch<ChatSearchResult>(`${base}/search?${params}`);
  },
  /** The threads the user follows, the latest reply first; `before` goes on after a thread's message. */
  threads: (before?: string) =>
    apiFetch<ChatThread[]>(`${base}/threads?limit=${CHAT_THREADS_PAGE}${before ? `&before=${before}` : ""}`),
  /** How many followed threads have replies new to the user. */
  unreadThreads: () => apiFetch<{ count: number }>(`${base}/threads/unread`),
  post: (channelId: string, body: string) =>
    apiFetch<ChatMessage>(`${base}/channels/${channelId}/messages`, { method: "POST", body: { body } }),
  postWithFiles: (channelId: string, body: string, files: File[]) =>
    sendFiles(`${base}/channels/${channelId}/messages/files`, body, files),
  edit: (messageId: string, body: string) =>
    apiFetch<ChatMessage>(`${base}/messages/${messageId}`, { method: "PATCH", body: { body } }),
  remove: (messageId: string) => apiFetch<void>(`${base}/messages/${messageId}`, { method: "DELETE" }),
  /** A message's thread: the message first, then its replies. */
  thread: (messageId: string) => apiFetch<ChatMessage[]>(`${base}/messages/${messageId}/replies`),
  reply: (messageId: string, body: string) =>
    apiFetch<ChatMessage>(`${base}/messages/${messageId}/replies`, { method: "POST", body: { body } }),
  replyWithFiles: (messageId: string, body: string, files: File[]) =>
    sendFiles(`${base}/messages/${messageId}/replies/files`, body, files),
  /** Starts or stops following a message's thread; the answer is the message with the thread's followers. */
  follow: (messageId: string, following: boolean) =>
    apiFetch<ChatMessage>(`${base}/messages/${messageId}/following`, { method: "PUT", body: { following } }),
  react: (messageId: string, emoji: string) =>
    apiFetch<ChatMessage>(`${base}/messages/${messageId}/reactions`, { method: "POST", body: { emoji } }),
  people: () => apiFetch<ChatPerson[]>(`${base}/people`),
};

/** What the stream says. `message` is new; `updated` is a correction or a reaction to one already there. */
export type ChatStreamEvent =
  | { type: "ready"; online: string[] }
  | { type: "online"; users: string[] }
  | { type: "message"; message: ChatMessage }
  | { type: "updated"; message: ChatMessage }
  | { type: "deleted"; channelId: string; messageId: string; parentId: string | null }
  | { type: "read"; channelId: string; lastReadAt: string | null }
  /** The user started or stopped following a thread, in this tab or another: the thread's message. */
  | { type: "followed"; message: ChatMessage }
  | { type: "channels"; channelId: string };

/** One SSE frame as the stream sends it, read into an event; null for anything the screen does not know. */
export function readStreamEvent(name: string, data: string): ChatStreamEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch {
    return null;
  }
  const p = payload as Record<string, unknown>;
  switch (name) {
    case "ready":
      return { type: "ready", online: (p.online as string[]) ?? [] };
    case "online":
      return { type: "online", users: (p.users as string[]) ?? [] };
    case "message":
    case "updated":
    case "followed":
      return { type: name, message: payload as ChatMessage };
    case "deleted":
      return { type: "deleted", channelId: String(p.channelId), messageId: String(p.messageId),
               parentId: (p.parentId as string) ?? null };
    case "read":
      return { type: "read", channelId: String(p.channelId), lastReadAt: (p.lastReadAt as string) ?? null };
    case "channels":
      return { type: "channels", channelId: String(p.channelId) };
    default:
      return null;
  }
}
